import React, { useEffect, useState } from 'react';

import DoneRounded from '@mui/icons-material/DoneRounded';
import GradingRounded from '@mui/icons-material/GradingRounded';
import RotateRightRounded from '@mui/icons-material/RotateRightRounded';
import {
  Badge, Grid, Stack,
} from '@mui/material';
import dayjs from 'dayjs';

import ContentCard from '../../../components/ContentCard';
import Tabs, { TabOption } from '../../../components/Tabs';
import { getCurrentUserId } from '../../../services/auth';
import { useGetExamAttemptsByUserQuery, useGetExamMarkingRequestsQuery } from '../../../services/exams';
import { ExamRequestStatus } from '../../../types/common';
import useTab from '../../../utils/react/hooks/useTab';

import AttemptsTab from './AttemptsTab';
import Leaderboard from './Leaderboard';
import RequestsTab from './RequestsTab';

enum Tab {
  Requests = 'requests',
  InProgress = 'in_progress',
  Done = 'done',
}

enum Label {
  Requests = 'Requests',
  InProgress = 'In Progress',
  Done = 'Done',
}

const TABS: TabOption[] = [
  {
    key: Tab.Requests,
    label: Label.Requests,
    icon: <GradingRounded />,
  },
  {
    key: Tab.InProgress,
    label: Label.InProgress,
    icon: <RotateRightRounded />,
  },
  {
    key: Tab.Done,
    label: Label.Done,
    icon: <DoneRounded />,
  },
];

const RootPage = () => {
  const userId = getCurrentUserId();

  const [tabs, setTabs] = useState<TabOption[]>(TABS);
  const [tab, setTab] = useTab({ default: tabs[0].key });

  const { data: examRequests } = useGetExamMarkingRequestsQuery(
    { marker: userId }, { skip: !userId },
  );
  const { data: examAttempts } = useGetExamAttemptsByUserQuery(
    { marker: userId }, { skip: !userId },
  );

  // Generate tab badges
  useEffect(() => {
    const newTabs = [...TABS];

    const requestsTab = newTabs.find((t) => t.key === Tab.Requests);

    // Requests tab badge
    if (requestsTab && examRequests) {
      const requests = examRequests.data.filter(
        (r) => r.status !== ExamRequestStatus.PendingApproval,
      );
      const overdueRequests = requests.filter((r) =>
        (r?.dueDatetime || Infinity) < dayjs().valueOf(),
      );

      requestsTab.label = (
        <Badge
          badgeContent={overdueRequests.length}
          color="error"
        >
          {Label.Requests}
        </Badge>
      );
    }

    setTabs(newTabs);
  }, [examRequests]);

  useEffect(() => {
    const newTabs = [...TABS];
    const inProgressTab = newTabs.find((t) => t.key === Tab.InProgress);

    // In Progress tab badge
    if (inProgressTab && examAttempts) {
      const attempts = examAttempts.data.filter((a) => !a.markedDatetime);
      const overdueAttempts = attempts.filter((a) =>
        (a?.dueDatetime || Infinity) < dayjs().valueOf(),
      );

      inProgressTab.label = (
        <Badge
          badgeContent={overdueAttempts.length}
          color="error"
        >
          {Label.InProgress}
        </Badge>
      );
    }

    setTabs(newTabs);
  }, [examAttempts]);

  return (
    <Stack width="100%" maxWidth="1080px">
      <Tabs
        options={tabs}
        selectedKey={tab}
        onChange={(key) => {
          setTab(key);
        }}
      />
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Stack alignItems="center">
            {tab === Tab.Requests && (<RequestsTab />)}
            {tab === Tab.InProgress && (
              <AttemptsTab
                title="Marking In Progress"
                isMarked={false}
              />
            )}
            {tab === Tab.Done && (
              <AttemptsTab
                title="Marked Exams"
                isMarked
              />
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <ContentCard title="Leaderboard" variant="outlined">
            <Leaderboard />
          </ContentCard>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default RootPage;
